const DEFAULT_SIZE = 24

const FONT_SIZES = [
  16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 46, 52, 62, 72, 82,
]

const BLOCK_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'td',
  'th',
  'caption',
])

const getRootBlock = (
  el: CKEDITOR.dom.node | null
): CKEDITOR.dom.element | null => {
  let block: CKEDITOR.dom.element | null = null

  let cursor: CKEDITOR.dom.element | null =
    el?.type === CKEDITOR.NODE_TEXT
      ? (el.getParent() as CKEDITOR.dom.element)
      : (el as CKEDITOR.dom.element | null)

  while (cursor?.type === CKEDITOR.NODE_ELEMENT) {
    const name = cursor.getName()

    if (name === 'ol' || name === 'ul') {
      return cursor
    }

    if (BLOCK_TAGS.has(name) && block === null) {
      block = cursor
    }

    cursor = cursor.getParent()
  }

  return block
}

const getSelectedRootBlocks = (
  editor: CKEDITOR.editor
): CKEDITOR.dom.element[] => {
  const selection = editor.getSelection()
  if (!selection) return []

  const ranges = selection.getRanges()
  const seen = new Set<string>()
  const blocks: CKEDITOR.dom.element[] = []

  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i]
    const walker = new CKEDITOR.dom.walker(range)

    walker.evaluator = (node: CKEDITOR.dom.node) => {
      if (node.type !== CKEDITOR.NODE_ELEMENT) return false

      const el = node as CKEDITOR.dom.element
      const name = el.getName()

      return BLOCK_TAGS.has(name) || name === 'ol' || name === 'ul'
    }

    const startBlock = getRootBlock(range.startContainer)
    if (startBlock) {
      const key = startBlock.getUniqueId().toString()

      if (!seen.has(key)) {
        seen.add(key)
        blocks.push(startBlock)
      }
    }

    const endBlock = getRootBlock(range.endContainer)

    if (endBlock) {
      const key = endBlock.getUniqueId().toString()

      if (!seen.has(key)) {
        seen.add(key)
        blocks.push(endBlock)
      }
    }

    let node = walker.next()

    while (node) {
      if (node.type === CKEDITOR.NODE_ELEMENT) {
        const el = node as CKEDITOR.dom.element
        const root = getRootBlock(el)

        if (root) {
          const key = root.getUniqueId().toString()

          if (!seen.has(key)) {
            seen.add(key)
            blocks.push(root)
          }
        }
      }
      node = walker.next()
    }
  }

  return blocks
}

const getCurrentSize = (editor: CKEDITOR.editor): number => {
  const startEl = editor.getSelection()?.getStartElement() ?? null
  const root = getRootBlock(startEl)

  if (root) {
    const size = +root.getAttribute('data-font-size')

    if (size) {
      return size
    }
  }

  return DEFAULT_SIZE
}

const applyFontSize = (editor: CKEDITOR.editor, size: number) => {
  editor.focus()
  editor.fire('saveSnapshot')

  let blocks = getSelectedRootBlocks(editor)

  if (blocks.length === 0) {
    const startEl = editor.getSelection()?.getStartElement() ?? null
    const block = getRootBlock(startEl)
    if (!block) {
      editor.fire('saveSnapshot')
      return
    }
    blocks = [block]
  }

  for (const block of blocks) {
    if (size === DEFAULT_SIZE) {
      block.removeAttribute('data-font-size')
    } else {
      block.setAttribute('data-font-size', String(size))
    }
  }

  editor.fire('saveSnapshot')
}

const promoteParagraphFontSizeToList = (editor: CKEDITOR.editor) => {
  const body = editor.document.getBody()
  const lists = body.find('ol, ul')

  for (let i = 0; i < lists.count(); i++) {
    const list = lists.getItem(i) as CKEDITOR.dom.element

    if (list.getAttribute('data-font-size')) {
      continue
    }

    const blocks = list.find('[data-font-size]')

    if (blocks.count() === 0) {
      continue
    }

    const firstBlock = blocks.getItem(0) as CKEDITOR.dom.element
    const size = firstBlock.getAttribute('data-font-size')

    if (!size) {
      continue
    }

    list.setAttribute('data-font-size', size)

    const nested = list.find('[data-font-size]')

    for (let j = 0; j < nested.count(); j++) {
      const el = nested.getItem(j) as CKEDITOR.dom.element

      el.removeAttribute('data-font-size')
    }
  }
}

export const fontSizeDropDownPlugin: CKEDITOR.pluginDefinition = {
  requires: 'richcombo',

  init(editor) {
    editor.addFeature({
      allowedContent: [
        'p[data-font-size]',
        'h1[data-font-size]',
        'h2[data-font-size]',
        'h3[data-font-size]',
        'h4[data-font-size]',
        'h5[data-font-size]',
        'h6[data-font-size]',
        'ol[data-font-size]',
        'ul[data-font-size]',
      ].join(';'),
    })

    editor.on('afterCommandExec', (evt) => {
      const commandName = evt.data.name as string

      if (
        ['numberedlist', 'bulletedlist', 'indent', 'outdent'].includes(
          commandName
        )
      ) {
        editor.fire('saveSnapshot')
        promoteParagraphFontSizeToList(editor)
        editor.fire('saveSnapshot')
      }
    })

    editor.ui.addRichCombo('FontSizeDropdown', {
      label: 'Font size',
      title: 'Font size',
      toolbar: 'insert',

      panel: {
        css: [
          CKEDITOR.skin.getPath('editor'),
          editor.config.contentsCss,
          `${CKEDITOR.basePath}plugins/fontsizedropdown.css`,
        ],
        multiSelect: false,
        attributes: { 'aria-label': 'Font size dropdown' },
      },

      init() {
        FONT_SIZES.forEach((size) => {
          this.add(size.toString(), `${size}\xa0px`)
        })
      },

      onClick(value: string) {
        applyFontSize(editor, +value)

        this.setValue(value, `${value}\xa0px`)
      },

      onRender() {
        editor.on('selectionChange', () => {
          const size = getCurrentSize(editor)
          this.setValue(size, `${size}\xa0px`)
        })
      },

      onOpen() {
        const size = getCurrentSize(editor)
        this.setValue(size, `${size}\xa0px`)
      },
    })
  },
}
